import type { ListItemButtonProps } from '@mui/material';
import { ListItemButton, ListItemText } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { FormattedPlural, defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import { LIVING_WILL } from '@constants/routes';

import { useRelatedPersons } from '@hooks/useRelatedPersons';

const messages = defineMessages({
  livingWillPOA: {
    id: 'legalMain.livingWillPOA',
    defaultMessage: 'Living Will / Power of Attorney for Personal Care',
  },
  docsUploaded: {
    id: 'legalMain.docsUploaded',
    defaultMessage: 'documents uploaded',
  },
  docUploaded: {
    id: 'legalMain.docUploaded',
    defaultMessage: 'document uploaded',
  },
  contactsListed: {
    id: 'legalMain.contactsListed',
    defaultMessage: 'contacts listed',
  },
  contactListed: {
    id: 'legalMain.contactListed',
    defaultMessage: 'contact listed',
  },
});

interface RelatedPersonListProps {
  hasError?: boolean;
}

const RelatedPersonList: React.FC<
  RouteComponentProps & RelatedPersonListProps
> = ({ hasError = false }) => {
  const { formatMessage } = useIntl();

  const { data: relatedPersonsData, loading: relatedPersonsLoading } =
    useRelatedPersons(hasError);
  const loading = relatedPersonsLoading;

  const { powerOfAttorney, legalCounsel } = relatedPersonsData;

  const numContacts =
    Number(Boolean(powerOfAttorney)) + Number(Boolean(legalCounsel));
  const attachments =
    powerOfAttorney?.attachments.concat(legalCounsel?.attachments ?? []) ?? [];

  const listItemButtonProps: ListItemButtonProps = {
    divider: true,
  };
  const secondaryAction = (
    <Icon color="text.primary" name="chevron-down" rotation={-90} type="fas" />
  );

  return (
    <ContentLoader loading={loading}>
      <ListItemButton
        {...listItemButtonProps}
        onClick={() => {
          navigate(LIVING_WILL);
        }}
      >
        <ListItemText
          primary={formatMessage(messages.livingWillPOA)}
          secondary={
            hasError ? null : (
              <>
                {attachments.length ? `${attachments.length} ` : ''}
                {attachments.length ? (
                  <FormattedPlural
                    one={formatMessage(messages.docUploaded)}
                    other={formatMessage(messages.docsUploaded)}
                    value={attachments.length}
                  />
                ) : (
                  ''
                )}

                {attachments.length && numContacts ? '. ' : ''}

                {numContacts ? `${numContacts} ` : ''}
                {numContacts ? (
                  <FormattedPlural
                    one={formatMessage(messages.contactListed)}
                    other={formatMessage(messages.contactsListed)}
                    value={numContacts}
                  />
                ) : (
                  ''
                )}
              </>
            )
          }
        />
        {secondaryAction}
      </ListItemButton>
    </ContentLoader>
  );
};

export default withErrorBoundary(RelatedPersonList, {
  fallback: <RelatedPersonList hasError />,
});
